import { useRouter } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { useEffect } from 'react';

import { useFeedbackToast } from 'common/hooks/useFeedbackToast';
import { useSelectedWarehouse } from 'common/hooks/useSelectedWarehouse';

import { WarehouseEvent, feedbackToastTriggerRules } from './feedbackToastTriggerRules';
import { numberOfEventsForFeedbackAtom } from './state';

type FeedbackToastProps = {
  eventName: WarehouseEvent;
  feedbackTitle: string;
  onSuccess?: VoidFunction;
  onDismiss?: VoidFunction;
};

/**
 A feedback toast is displayed once when the feedbackTriggerRules satisfy and the toast is re-triggered in the next specified date.
 */

export const useWarehouseFeedbackToast = ({
  eventName,
  feedbackTitle,
  onSuccess,
  onDismiss,
}: FeedbackToastProps) => {
  const { showFeedbackToast, feedbackToast } = useFeedbackToast();
  const { id: warehouseId } = useSelectedWarehouse();

  const {
    history: { location },
  } = useRouter();

  // numberOfEvents corresponds to a specific warehouse event. E.g. Receiving, Shrinkage or Adjustment.
  // On mutation, we keep track of events with useSetEventNumberForFeedback.
  // If the value of numberOfEventsForFeedbackAtom is greater than minNumberOfEvents defined in triggerRules, a feedback toast is displayed.

  const [numberOfEvents, setNumberOfEvents] = useAtom(numberOfEventsForFeedbackAtom);
  const { id, daysForRetrigger, minNumberOfEvents } = feedbackToastTriggerRules[eventName];

  useEffect(() => {
    if (warehouseId) {
      showFeedbackToast({
        id,
        title: feedbackTitle,
        operationType: eventName,
        expireDays: daysForRetrigger,
        enabled: numberOfEvents >= minNumberOfEvents,
        warehouseId,
        onSuccess,
        onDismiss,
      });
    }

    return () => {
      // When there is a location change, we reset the numberOfEventsForFeedbackAtom to 0.
      // This ensures that the counter starts again from 0 in another event.
      const newLocationRoot = location.pathname.split('/')[1];

      if (!newLocationRoot.includes(eventName)) {
        setNumberOfEvents(0);
      }
    };
  }, [
    daysForRetrigger,
    eventName,
    feedbackTitle,
    id,
    location.pathname,
    minNumberOfEvents,
    numberOfEvents,
    onDismiss,
    onSuccess,
    setNumberOfEvents,
    showFeedbackToast,
    warehouseId,
  ]);

  return { isFeedbackToastShown: feedbackToast?.id === id };
};
